import PrimaryLabel from '@/views/components/Labels/PrimaryLabel';
import Drawer from '@/views/components/Modals/Drawer';
import { Document, pdf } from '@react-pdf/renderer';
import { Col, Form, Popconfirm, Row } from 'antd';
import { saveAs } from 'file-saver';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { ReactComponent as SettingsIcon } from '../../../../../assets/icons/Settings.svg';
import { ReactComponent as BoxIcon } from '../../../../../assets/icons/box-icon.svg';
import { ReactComponent as DeleteIcon } from '../../../../../assets/icons/delete-icon.svg';
import { ReactComponent as ExportIcon } from '../../../../../assets/icons/export-icon.svg';
import { ReactComponent as TableIcon } from '../../../../../assets/icons/log-file.svg';
import { SEARCH_VIEW_TYPES } from '../../../../../contants/common';
import { APP_ROUTES } from '../../../../../contants/routes';
import {
  deleteLossAndDamageClaimsInProgress,
  setLDClaimViewType,
  setSelectedLADClaims,
} from '../../../../../redux/Claims/LossAndDamage/LossAndDamageClaimsActions';
import { setAppLoader } from '../../../../../redux/common/CommonActions';
import { routeFileSelector } from '../../../../../utilz/i18n';
import Button from '../../../../components/Buttons/Button';
import IconButton from '../../../../components/Buttons/IconButton';
import TextButton from '../../../../components/Buttons/TextButton';
import SvgConverter from '../../../../components/Common/SvgConverter';
import FormCheckBox from '../../../../components/Inputs/FormCheckBox';
import CommonModal from '../../../../components/Modals/CommonModal';
import ContentSpinner from '../../../../components/Spinners/ContentSpinner';
import LADClaimPDF from '../../../Print/Claims/LossAndDamage/LADClaimPDF';
import { useGetLADPrintForm } from '../../hooks';
import LADMultiplePrintClaims from '../Prints/LADMultiplePrintClaims';
import LDClaimTableViewFieldsDrawer from '../TableViewFieldsOptions/LDClaimTableViewFieldsDrawer';
import LADExportForm from './LADExportForm';
import LossAndDamageSorting from './LossAndDamageSorting';

const LossAndDamageHeader = () => {
  const dispatch = useDispatch();
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [exportModal, setExportModal] = useState(false);
  const [printModal, setPrintModal] = useState(false);
  const [exportForm] = Form.useForm();
  const [tableViewFieldsDrawer, setTableViewFieldsDrawer] = useState(false);

  const { claimsList, selectedClaims, claimViewMode } = useSelector((state) => state.lossAndDamage);

  const {
    data: claimDetailsBatch = [],
    refetch: refetchClaimDetailsBatch,
    isLoading: isClaimDetailsBatchLoading,
  } = useGetLADPrintForm({
    ClaimKeys: selectedClaims.join(','),
    enabled: false,
  });

  const handleCheckBoxClick = (val) => {
    if (val) {
      if (val.target.checked) {
        dispatch(setSelectedLADClaims(claimsList.data.map(({ claimKey }) => claimKey)));
      } else {
        dispatch(setSelectedLADClaims([]));
      }
    }
  };

  const actionButtonsDisabled = useMemo(() => !selectedClaims?.length, [selectedClaims?.length]);

  const handleDelete = () => {
    dispatch(deleteLossAndDamageClaimsInProgress(selectedClaims ? selectedClaims.join(',') : ''));
  };

  const handlePrintForm = () => {
    refetchClaimDetailsBatch();
    setPrintModal(true);
  };

  const handleGeneratePDF = async () => {
    try {
      if (!claimDetailsBatch?.length) return false;
      dispatch(setAppLoader(true));
      const fileName = 'Claim Details.pdf';
      const blob = await pdf(<PDFdocument claims={claimDetailsBatch} />).toBlob();
      saveAs(blob, fileName);
      dispatch(setAppLoader(false));
    } catch (error) {
      console.log(error);
      dispatch(setAppLoader(false));
    }
  };

  const handleShipmentViewToggle = () => {
    dispatch(
      setLDClaimViewType(claimViewMode === SEARCH_VIEW_TYPES.CARD ? SEARCH_VIEW_TYPES.TABLE : SEARCH_VIEW_TYPES.CARD),
    );
  };

  return (
    <Row className="px-15 py-15" justify="space-between" align="middle">
      <Col>
        <Row gutter={20} align="middle">
          <Col>
            <FormCheckBox
              className="m-0 pl-1"
              checkBoxClass="normal-checkbox"
              onChange={handleCheckBoxClick}
              checked={claimsList.data.length > 0 && claimsList.data.length === selectedClaims.length}
              label={<span className="ml-5 f-14 text-black">{t('translation:default.fields.selectAll')}</span>}
            />
          </Col>
          <Col>
            <Button
              type="default"
              onClick={() => handleCheckBoxClick({ target: { checked: false } })}
              className="clear-selection-button p-0 ml-n5"
            >
              {t('translation:default.buttons.clearSelection')}
            </Button>
          </Col>
          <Col style={{ width: 400 }}>
            <LossAndDamageSorting />
          </Col>
        </Row>
      </Col>
      <Col>
        <Row align="middle" gutter={20}>
          <Col>
            <Link to={APP_ROUTES.CREATE_LOSS_AND_DAMAGE_CLAIMS}>
              <Button>Create</Button>
            </Link>
          </Col>
          <Col>
            <Popconfirm
              placement="topRight"
              title={t('translation:default.fields.deleteConfirm.title')}
              onConfirm={handleDelete}
              okText={t('translation:default.options.yes')}
              cancelText={t('translation:default.buttons.cancel')}
              overlayClassName=""
            >
              <TextButton icon={<SvgConverter Icon={DeleteIcon} />} disabled={actionButtonsDisabled}>
                {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
                <label className="ml-2">{t('translation:default.buttons.delete')}</label>
              </TextButton>
            </Popconfirm>
          </Col>
          <Col>
            <TextButton
              icon={<SvgConverter Icon={ExportIcon} />}
              disabled={!claimsList?.data?.length}
              onClick={() => setExportModal(true)}
            >
              {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
              <label className="ml-2">Print Results</label>
            </TextButton>
          </Col>
          <Col>
            <TextButton
              icon={<SvgConverter Icon={ExportIcon} />}
              disabled={actionButtonsDisabled}
              onClick={handlePrintForm}
            >
              {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
              <label className="ml-2">Print Forms</label>
            </TextButton>
          </Col>
          <Col>
            <IconButton onClick={handleShipmentViewToggle}>
              <SvgConverter
                Icon={claimViewMode === SEARCH_VIEW_TYPES.CARD ? TableIcon : BoxIcon}
                color="header"
                width={22}
                height={22}
              />
            </IconButton>
          </Col>
          {claimViewMode === SEARCH_VIEW_TYPES.TABLE ? (
            <Col>
              <TextButton
                onClick={() => setTableViewFieldsDrawer(true)}
                icon={<SvgConverter Icon={SettingsIcon} height={22} width={22} />}
              >
                {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
                <label className="ml-0">Column Options</label>
              </TextButton>
            </Col>
          ) : (
            <></>
          )}
        </Row>
      </Col>
      <CommonModal
        className="vw-80"
        open={exportModal}
        title="Loss And Damage Export"
        okText={t('translation:default.buttons.export')}
        cancelText={t('translation:default.buttons.close')}
        onCancel={() => setExportModal(false)}
        onClose={() => setExportModal(false)}
        onOk={() => exportForm.submit()}
        afterOpenChange={(val) => {
          if (!val) {
            exportForm.resetFields();
          }
        }}
      >
        <LADExportForm form={exportForm} setExportModal={setExportModal} />
      </CommonModal>
      <CommonModal
        className="vw-80"
        open={printModal}
        title="Loss And Damage Print"
        okText="Print"
        cancelText={t('translation:default.buttons.close')}
        onCancel={() => setPrintModal(false)}
        onClose={() => setPrintModal(false)}
        onOk={handleGeneratePDF}
        afterOpenChange={(val) => {
          if (!val) {
            exportForm.resetFields();
          }
        }}
      >
        <div className="h-100 custom-scroll-bar">
          {isClaimDetailsBatchLoading ? (
            <ContentSpinner size="large" />
          ) : (
            <LADMultiplePrintClaims claims={claimDetailsBatch ? claimDetailsBatch : []} />
          )}
        </div>
      </CommonModal>
      <Drawer
        visible={tableViewFieldsDrawer}
        onClose={() => setTableViewFieldsDrawer(false)}
        headerContents={<PrimaryLabel className="text-white f-16">Column Options</PrimaryLabel>}
        width="400px"
        destroyOnClose
      >
        <LDClaimTableViewFieldsDrawer onClose={() => setTableViewFieldsDrawer(false)} />
      </Drawer>
    </Row>
  );
};

export default LossAndDamageHeader;

const PDFdocument = ({ claims }) => {
  return (
    <Document>
      {claims.map((data) => (
        <LADClaimPDF key={data.claimNumber} claimDetails={data} />
      ))}
    </Document>
  );
};
